import { useCallback, useState } from 'react'

import { Box, Card, CardContent, Stack, Typography } from '@mui/material'
import {
  AutocompleteInput,
  Button,
  Datagrid,
  FunctionField,
  Link,
  List,
  RaRecord,
  ReferenceInput,
  SearchInput,
  TextField,
  TopToolbar,
  useRefresh,
  useTranslate,
} from 'react-admin'

import { Tooltip } from '@mantine/core'
import { LibraryAdd } from '@mui/icons-material'
import { ArtworkApiDTO, CompilationApiDTO } from '../../codegen/generated/Api.schemas'
import { CreateCompilationModal } from '../../components/Compilations/CreateCompilationModal'
import { DeleteCompilation } from '../../components/Compilations/DeleteCompilation'
import { EditCompilation } from '../../components/Compilations/EditCompilation'
import { API } from '../../helpers/Api.helper'
import { ListHelper } from '../../helpers/List.helper'
import { useUserStore } from '../../stores/useUserStore'
import { Forbidden } from '../Forbidden'
import styles from './Compilations.module.scss'

export const Compilations = () => {
  const translate = useTranslate()
  const refresh = useRefresh()
  const userStore = useUserStore()

  const [isCreateCompilationOpen, setIsCreateCompilationOpen] = useState(false)

  const onCreate = useCallback(() => {
    refresh()
  }, [refresh])

  const CreateCompilationButton = (): JSX.Element => (
    <Button
      label={translate('pages.compilations.createCompilation.title')}
      onClick={() => setIsCreateCompilationOpen(true)}
      variant="contained"
      disabled={!userStore.isAuthorised('compilations.edit')}
    >
      <LibraryAdd />
    </Button>
  )

  if (!userStore.isAuthorised('compilations.read')) {
    return <Forbidden permission="compilations.read" />
  }

  return (
    <Card>
      <CardContent className={styles.container}>
        <List
          resource="compilations"
          exporter={false}
          perPage={API.DEFAULT_PAGINATION.perPage}
          sort={{ field: 'name', order: 'DESC' }}
          actions={
            <TopToolbar>
              <Tooltip
                label={`⚠ ${translate('pages.users.lackingPermission')} → ${translate(
                  'pages.users.permissionsList.compilations.edit',
                )}`}
                hidden={userStore.isAuthorised('compilations.edit')}
              >
                <span>
                  <CreateCompilationButton />
                </span>
              </Tooltip>
            </TopToolbar>
          }
          empty={
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              padding={10}
              width="100%"
            >
              <CreateCompilationButton />
            </Box>
          }
          filters={[
            <SearchInput key="search" source="q" alwaysOn />,
            <ReferenceInput
              key="mainArtwork"
              source="mainArtworkId"
              reference="artworks"
              alwaysOn
              allowEmpty={false}
              sort={ListHelper.DEFAULT_SORT}
            >
              <AutocompleteInput
                optionText="name"
                label={translate('pages.compilations.mainArtwork')}
              />
            </ReferenceInput>,
          ]}
        >
          <Datagrid bulkActionButtons={false} rowClick={false} hover={false}>
            <TextField source="name" label={translate('pages.compilations.compilationName')} />
            <FunctionField
              source="mainArtwork"
              sortable={false}
              label={translate('pages.compilations.mainArtwork')}
              render={(record: RaRecord | undefined) => (
                <Link to={`/artworks/${record?.mainArtwork.id}/show`}>
                  {record?.mainArtwork.name}
                </Link>
              )}
            />
            <FunctionField
              source="artworks"
              sortable={false}
              label={translate('pages.compilations.linkedArtworks')}
              render={(record: RaRecord | undefined) => (
                <Stack>
                  {record?.artworks.slice(0, 2).map((a: ArtworkApiDTO) => (
                    <Link to={`/artworks/${a.id}/show`} width="fit-content">
                      {a.name}
                    </Link>
                  ))}
                  {record?.artworks.length > 2 && (
                    <Typography component="i" fontSize={14}>
                      {translate('_generics.andXOthers', {
                        smart_count: record?.artworks.length - 2,
                      })}
                    </Typography>
                  )}
                </Stack>
              )}
            />
            {userStore.isAuthorised('compilations.edit') && (
              <FunctionField
                source=""
                sortable={false}
                label=""
                width={150}
                render={(record: RaRecord | undefined) => (
                  <>
                    <EditCompilation
                      compilationId={(record?.id ?? '') as CompilationApiDTO['id']}
                      compilationName={record?.name ?? ''}
                      onSuccess={() => refresh()}
                    />
                    <DeleteCompilation
                      compilationId={record?.id.toString() ?? ''}
                      compilationName={record?.name ?? ''}
                      onDelete={() => refresh()}
                    />
                  </>
                )}
              />
            )}
          </Datagrid>
        </List>
        <CreateCompilationModal
          isModalOpen={isCreateCompilationOpen}
          setIsModalOpen={setIsCreateCompilationOpen}
          onSuccess={onCreate}
        />
      </CardContent>
    </Card>
  )
}
